import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar, Toolbar, Box, Button, Container, IconButton,
  Drawer, List, ListItem, ListItemButton, ListItemText,
  Divider, Menu, MenuItem,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  KeyboardArrowDown,
  LocalShipping,
  DirectionsCar,
} from '@mui/icons-material';

const COURIER_URL = process.env.REACT_APP_COURIER_URL || 'http://localhost:3000';
const DRIVER_URL = process.env.REACT_APP_DRIVER_URL || 'http://localhost:3001';

const navLinks = [
  { label: 'Funcionalidades', href: '#features' },
  { label: 'Como funciona', href: '#how-it-works' },
  { label: 'Preços', href: '#pricing' },
  { label: 'Testemunhos', href: '#testimonials' },
];

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [loginMenuAnchor, setLoginMenuAnchor] = useState<null | HTMLElement>(null);
  const [getStartedAnchor, setGetStartedAnchor] = useState<null | HTMLElement>(null);

  /** Hero is dark only on home; other routes need solid bar + dark text from the top */
  const isHome = location.pathname === '/';
  const solidNav = !isHome || scrolled;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  const scrollToHash = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleNavClick = (href: string) => {
    setDrawerOpen(false);
    if (!href.startsWith('#')) return;
    if (location.pathname !== '/') {
      navigate({ pathname: '/', hash: href });
    } else {
      scrollToHash(href);
    }
  };

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          background: solidNav
            ? 'rgba(255,255,255,0.97)'
            : 'transparent',
          backdropFilter: solidNav ? 'blur(12px)' : 'none',
          borderBottom: solidNav ? '1px solid rgba(0,0,0,0.08)' : 'none',
          transition: 'background 0.3s ease, border-color 0.3s ease, backdrop-filter 0.3s ease',
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ height: 72, gap: 2 }}>
            {/* Logo */}
            <Box
              sx={{ display: 'flex', alignItems: 'center', flex: '0 0 auto', cursor: 'pointer' }}
              onClick={() => {
                navigate('/');
                window.setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50);
              }}
            >
              <Box
                component="img"
                src={solidNav ? '/logo.svg' : '/logo-white.svg'}
                alt="NeoDrive"
                sx={{ height: 60, transition: 'all 0.3s ease' }}
              />
            </Box>

            {/* Desktop nav links */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 0.5, mx: 'auto' }}>
              {navLinks.map((link) => (
                <Button
                  key={link.label}
                  onClick={() => handleNavClick(link.href)}
                  sx={{
                    color: solidNav ? 'text.primary' : 'rgba(255,255,255,0.9)',
                    fontWeight: 500,
                    fontSize: '0.9rem',
                    px: 2,
                    '&:hover': {
                      color: solidNav ? 'primary.main' : '#fff',
                      background: solidNav
                        ? 'rgba(30,64,175,0.06)'
                        : 'rgba(255,255,255,0.1)',
                    },
                  }}
                >
                  {link.label}
                </Button>
              ))}
            </Box>

            {/* Desktop CTAs */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1.5, alignItems: 'center' }}>
              <Button
                endIcon={<KeyboardArrowDown />}
                onClick={(e) => setLoginMenuAnchor(e.currentTarget)}
                sx={{
                  color: solidNav ? 'text.primary' : 'rgba(255,255,255,0.9)',
                  fontWeight: 500,
                  '&:hover': {
                    background: solidNav
                      ? 'rgba(0,0,0,0.04)'
                      : 'rgba(255,255,255,0.1)',
                  },
                }}
              >
                Iniciar sessão
              </Button>
              <Menu
                anchorEl={loginMenuAnchor}
                open={Boolean(loginMenuAnchor)}
                onClose={() => setLoginMenuAnchor(null)}
                PaperProps={{
                  sx: {
                    mt: 1,
                    borderRadius: 2,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                    border: '1px solid rgba(0,0,0,0.06)',
                    minWidth: 200,
                  },
                }}
              >
                <MenuItem
                  component="a"
                  href={COURIER_URL}
                  onClick={() => setLoginMenuAnchor(null)}
                  sx={{ gap: 1.5, py: 1.5, '&:hover': { color: '#16a34a' } }}
                >
                  <LocalShipping fontSize="small" sx={{ color: '#16a34a' }} />
                  <Box>
                    <Box sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Painel de estafetas</Box>
                    <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>Para empresas de estafetas</Box>
                  </Box>
                </MenuItem>
                <Divider />
                <MenuItem
                  component="a"
                  href={DRIVER_URL}
                  onClick={() => setLoginMenuAnchor(null)}
                  sx={{ gap: 1.5, py: 1.5, '&:hover': { color: '#ea580c' } }}
                >
                  <DirectionsCar fontSize="small" sx={{ color: '#ea580c' }} />
                  <Box>
                    <Box sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Painel de motoristas</Box>
                    <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>Para operadores de frota</Box>
                  </Box>
                </MenuItem>
              </Menu>

              <Button
                variant="contained"
                endIcon={<KeyboardArrowDown />}
                onClick={(e) => setGetStartedAnchor(e.currentTarget)}
                sx={{
                  background: 'linear-gradient(135deg, #16a34a, #15803d)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #15803d, #166534)',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 8px 20px rgba(22,163,74,0.35)',
                  },
                }}
              >
                Começar grátis
              </Button>
              <Menu
                anchorEl={getStartedAnchor}
                open={Boolean(getStartedAnchor)}
                onClose={() => setGetStartedAnchor(null)}
                PaperProps={{
                  sx: {
                    mt: 1,
                    borderRadius: 2,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                    border: '1px solid rgba(0,0,0,0.06)',
                    minWidth: 220,
                  },
                }}
              >
                <MenuItem
                  component="a"
                  href={`${COURIER_URL}/register`}
                  onClick={() => setGetStartedAnchor(null)}
                  sx={{ gap: 1.5, py: 1.5, '&:hover': { color: '#16a34a' } }}
                >
                  <LocalShipping fontSize="small" sx={{ color: '#16a34a' }} />
                  <Box>
                    <Box sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Registo — Estafetas</Box>
                    <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>Conta de empresa de estafetas</Box>
                  </Box>
                </MenuItem>
                <Divider />
                <MenuItem
                  component="a"
                  href={`${DRIVER_URL}/register`}
                  onClick={() => setGetStartedAnchor(null)}
                  sx={{ gap: 1.5, py: 1.5, '&:hover': { color: '#ea580c' } }}
                >
                  <DirectionsCar fontSize="small" sx={{ color: '#ea580c' }} />
                  <Box>
                    <Box sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Registo — Motoristas</Box>
                    <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>Plataforma de frota / motoristas</Box>
                  </Box>
                </MenuItem>
              </Menu>
            </Box>

            {/* Mobile menu button */}
            <IconButton
              sx={{
                display: { xs: 'flex', md: 'none' },
                ml: 'auto',
                color: solidNav ? 'text.primary' : '#fff',
              }}
              onClick={() => setDrawerOpen(true)}
            >
              <MenuIcon />
            </IconButton>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: 300, p: 2 } }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box component="img" src="/logo.svg" alt="NeoDrive" sx={{ height: 32 }} />
          <IconButton onClick={() => setDrawerOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ mb: 1 }} />
        <List>
          {navLinks.map((link) => (
            <ListItem key={link.label} disablePadding>
              <ListItemButton
                onClick={() => handleNavClick(link.href)}
                sx={{ borderRadius: 2, mb: 0.5 }}
              >
                <ListItemText
                  primary={link.label}
                  primaryTypographyProps={{ fontWeight: 500 }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Button
            variant="outlined"
            fullWidth
            href={COURIER_URL}
            startIcon={<LocalShipping />}
            sx={{
              borderColor: '#16a34a',
              color: '#16a34a',
              '&:hover': { borderColor: '#15803d', background: 'rgba(22,163,74,0.06)' },
            }}
          >
            Iniciar sessão — Estafetas
          </Button>
          <Button
            variant="outlined"
            fullWidth
            href={DRIVER_URL}
            startIcon={<DirectionsCar />}
            sx={{
              borderColor: '#ea580c',
              color: '#ea580c',
              '&:hover': { borderColor: '#c2410c', background: 'rgba(234,88,12,0.06)' },
            }}
          >
            Iniciar sessão — Motoristas
          </Button>
          <Button
            variant="outlined"
            fullWidth
            href={`${COURIER_URL}/register`}
            startIcon={<LocalShipping />}
            sx={{
              borderColor: '#16a34a',
              color: '#16a34a',
              '&:hover': { borderColor: '#15803d', background: 'rgba(22,163,74,0.06)' },
            }}
          >
            Registo — Estafetas
          </Button>
          <Button
            variant="outlined"
            fullWidth
            href={`${DRIVER_URL}/register`}
            startIcon={<DirectionsCar />}
            sx={{
              borderColor: '#ea580c',
              color: '#ea580c',
              '&:hover': { borderColor: '#c2410c', background: 'rgba(234,88,12,0.06)' },
            }}
          >
            Registo — Motoristas
          </Button>
        </Box>
      </Drawer>
    </>
  );
}
